import { z } from 'zod'

import { legacySiteId } from '../../index.js'

// Label for annotated map - positioned text overlay (used in 'uploaded' mode)
export const annotatedMapLabel = z.object({
  id: z.string(), // Unique label ID
  x: z.number().min(0).max(100), // X position as percentage of image width
  y: z.number().min(0).max(100), // Y position as percentage of image height
  placeholderText: z.string().optional(), // Static text for testing (before site data wiring)
  siteId: legacySiteId.optional(), // Future: link to site for data-driven text
})

export type AnnotatedMapLabel = z.infer<typeof annotatedMapLabel>

// Label style configuration (shared between modes)
export const annotatedMapLabelStyle = z.object({
  backgroundColor: z.string().default('#CC0000'), // Red box
  textColor: z.string().default('#FFFFFF'), // White text
  fontSize: z.number().default(14),
  padding: z.number().default(6),
  borderRadius: z.number().default(4),
})

export type AnnotatedMapLabelStyle = z.infer<typeof annotatedMapLabelStyle>

// Generated map configuration (for 'generated' mode using Mapbox)
export const generatedMapConfig = z.object({
  mapStyle: z
    .enum(['satellite', 'satellite-streets', 'outdoors', 'streets'])
    .default('satellite-streets'),
  // Padding percentage around the markers (0-50)
  padding: z.number().min(0).max(50).default(10),
  // Image dimensions
  width: z.number().min(200).max(1280).default(800),
  height: z.number().min(200).max(1280).default(600),
  // Optional manual bounding box override (if not set, auto-fit to sites)
  boundingBox: z
    .object({
      west: z.number(),
      south: z.number(),
      east: z.number(),
      north: z.number(),
    })
    .optional(),
  // Marker customization
  markerColor: z.string().default('CC0000'), // Hex color without #
  markerSize: z.enum(['s', 'l']).default('l'), // small or large
  // Label offset from marker (pixels)
  labelOffset: z
    .object({
      x: z.number().default(0),
      y: z.number().default(-25), // Default above the marker
    })
    .optional(),
})

export type GeneratedMapConfig = z.infer<typeof generatedMapConfig>

// Configuration for annotated map section - supports both uploaded and generated modes
export const annotatedMapConfig = z.object({
  // Mode toggle: 'uploaded' for manual image upload, 'generated' for Mapbox-based auto-generation
  mode: z.enum(['uploaded', 'generated']).default('uploaded'),

  // === Fields for 'uploaded' mode (original functionality) ===
  baseImage: z.string().optional(), // Base64-encoded image data (without data: prefix)
  baseImageMimeType: z
    .enum(['image/png', 'image/jpeg', 'image/gif'])
    .optional(),
  labels: z.array(annotatedMapLabel).optional(), // Manually positioned labels

  // === Fields for 'generated' mode (new Mapbox functionality) ===
  generatedConfig: generatedMapConfig.optional(),

  // === Shared fields ===
  caption: z.string().optional(), // Figure caption below the map
  labelStyle: annotatedMapLabelStyle.optional(),
})

export type AnnotatedMapConfig = z.infer<typeof annotatedMapConfig>

// Section-specific configuration (stored in config JSONB)
// Used for markdown content, annotated maps, and other per-section overrides
export const reportSectionConfig = z
  .object({
    // Markdown content (for configurationStaticText, wivExecutiveSummary, wivSensorSettings, etc.)
    markdown: z.string().optional(),

    // Annotated map configuration
    annotatedMap: annotatedMapConfig.optional(),

    // Allow additional section-specific properties
  })
  .passthrough()

export type ReportSectionConfig = z.infer<typeof reportSectionConfig>

// Section config structure - keyed by section ID
// Example: { "serviceManagerSummary": { "markdown": "**Bold** text..." }, "openingDatesMap": { "annotatedMap": {...} } }
export const reportConfigurationData = z.record(
  z.string(), // section ID
  reportSectionConfig,
)

export type ReportConfigurationData = z.infer<typeof reportConfigurationData>

// Logo configuration for report header
// Stored at configuration level (not per-section) as config.headerLogo
export const reportLogoConfig = z.object({
  headerLogo: z.string().optional(), // Base64-encoded image data (without data: prefix)
  headerLogoMimeType: z
    .enum(['image/png', 'image/jpeg', 'image/gif'])
    .optional(),
})

export type ReportLogoConfig = z.infer<typeof reportLogoConfig>
